import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  orderBy, 
  query,
  getDoc
} from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { useAdminAuth } from '../Provider/AdminAuthProvider';
import { 
  FaEye, 
  FaCheck, 
  FaTimes, 
  FaBuilding, 
  FaExternalLinkAlt, 
  FaChevronUp, 
  FaChevronDown, 
  FaChartBar,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaGlobe,
  FaLinkedin,
  FaFileAlt,
  FaCalendarAlt,
  FaUsers,
  FaIndustry,
  FaShieldAlt,
  FaExclamationTriangle,
  FaStickyNote,
  FaDownload
} from 'react-icons/fa';
import AdminLayout from '../components/AdminLayout';
import { emailNotifications } from '../../components/notifications/EmailNotificationComponent';

export const EmployerVerification = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const { user } = useAdminAuth();

  // Fetch applications from employer_applications collection
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'employer_applications'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const apps = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApplications(apps);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // Update application status - sync authentication fields to employers collection
  const updateApplicationStatus = async (applicationId, status, notes = '') => {
    try {
      setActionLoading(true);
      const applicationRef = doc(db, 'employer_applications', applicationId);
      const application = applications.find(app => app.id === applicationId);
      
      if (!application) {
        throw new Error('Application not found');
      }

      const updateData = {
        verificationStatus: status,
        updatedAt: new Date(),
        verificationNotes: notes ? [
          ...(application.verificationNotes || []), 
          {
            note: notes,
            timestamp: new Date(),
            action: status,
            adminId: user?.user?.uid // Replace with actual admin ID
          }
        ] : application.verificationNotes || []
      };

      // Update based on status
      if (status === 'approved') {
        updateData.isActive = true;
        updateData.canLogin = true;
        updateData.approvedAt = new Date();
        updateData.registrationPhase = 'completed';
      } else if (status === 'rejected') {
        updateData.rejectionReason = notes;
        updateData.isActive = false;
        updateData.canLogin = false;
        updateData.rejectedAt = new Date();
      } else if (status === 'under_review') {
        updateData.reviewStartedAt = new Date();
        updateData.isActive = false;
        updateData.canLogin = true;
      }

      // Update employer_applications collection
      await updateDoc(applicationRef, updateData);
      
      // CRITICAL: Sync authentication fields to employers collection for login verification
      try {
        const employerRef = doc(db, 'employers', application.uid);
        const employerDoc = await getDoc(employerRef);
        
        if (employerDoc.exists()) {
          const authUpdateData = {
            verificationStatus: status,
            isActive: updateData.isActive,
            canLogin: updateData.canLogin,
            updatedAt: new Date()
          };
          await updateDoc(employerRef, authUpdateData);
          console.log(`✅ Synced authentication status to employers collection for ${application.companyName}`);
        } else {
          console.warn(`⚠️ Employer profile not found for UID: ${application.uid}`);
        }
      } catch (syncError) {
        console.error('❌ Error syncing to employers collection:', syncError);
        // Don't fail the entire operation if sync fails
        alert(`Application ${status} successfully, but there was an issue syncing authentication status. Please check the employer profile manually.`);
      }

      if (status === 'approved' || status === 'rejected') {
        try {
          const employerData = {
            email: application.companyEmail || application.email,
            companyName: application.companyName,
            contactPerson: application.contactPerson
          };
          
          await emailNotifications.notifyEmployerVerification(employerData, status, notes);
          console.log(`✅ Email notification sent to ${employerData.companyName}`);
        } catch (emailError) {
          console.error('❌ Error sending email notification:', emailError);
          // Don't fail the entire operation if email fails
        }
      }
      
      // Refresh applications list
      await fetchApplications();
      setShowModal(false);
      setSelectedApplication(null);
      setActionNotes('');
      
      alert(`Application ${status} successfully!`);
    } catch (error) {
      console.error('Error updating application:', error);
      alert('Error updating application status: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Filter applications based on search and status
  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.legalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.contactPersonName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || app.verificationStatus === filter;
    
    return matchesSearch && matchesFilter;
  });

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'under_review': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    // Simple formatting for display
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  // Application Details Modal Component
  const ApplicationDetailsModal = ({ application, onClose, onUpdateStatus }) => {
    if (!application) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{application.companyName}</h2>
              <p className="text-gray-600">{application.legalName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <div className="p-6 space-y-8">
            {/* Status and Risk Level */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Verification Status</h4>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.verificationStatus)}`}>
                  {application.verificationStatus?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Risk Level</h4>
                <span className={`font-semibold ${getRiskLevelColor(application.riskLevel)}`}>
                  <FaShieldAlt className="inline mr-1" />
                  {application.riskLevel?.toUpperCase()}
                </span>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Registration Phase</h4>
                <span className="text-gray-900 font-medium">
                  {application.registrationPhase?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>

            {/* Company Information */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaBuilding className="mr-2 text-indigo-600" />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Industry</label>
                  <p className="mt-1 text-gray-900 flex items-center">
                    <FaIndustry className="mr-2 text-gray-500" />
                    {application.industry}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Size</label>
                  <p className="mt-1 text-gray-900 flex items-center">
                    <FaUsers className="mr-2 text-gray-500" />
                    {application.companySize}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Founded Year</label>
                  <p className="mt-1 text-gray-900 flex items-center">
                    <FaCalendarAlt className="mr-2 text-gray-500" />
                    {application.foundedYear}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Website</label>
                  <p className="mt-1 text-gray-900 flex items-center">
                    <FaGlobe className="mr-2 text-gray-500" />
                    {application.website ? (
                      <a href={application.website} target="_blank" rel="noopener noreferrer" 
                         className="text-indigo-600 hover:underline">
                        {application.website}
                      </a>
                    ) : 'Not provided'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">LinkedIn Profile</label>
                  <p className="mt-1 text-gray-900 flex items-center">
                    <FaLinkedin className="mr-2 text-gray-500" />
                    {application.linkedinProfile ? (
                      <a href={application.linkedinProfile} target="_blank" rel="noopener noreferrer" 
                         className="text-indigo-600 hover:underline">
                        View Profile
                      </a>
                    ) : 'Not provided'}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaPhone className="mr-2 text-indigo-600" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                  <p className="mt-1 text-gray-900">{application.contactPersonName}</p>
                  <p className="text-sm text-gray-600">{application.contactPersonTitle}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-gray-900 flex items-center">
                    <FaEnvelope className="mr-2 text-gray-500" />
                    <a href={`mailto:${application.email}`} className="text-indigo-600 hover:underline">
                      {application.email}
                    </a>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="mt-1 text-gray-900 flex items-center">
                    <FaPhone className="mr-2 text-gray-500" />
                    <a href={`tel:${application.phone}`} className="text-indigo-600 hover:underline">
                      {formatPhoneNumber(application.phone)}
                    </a>
                  </p>
                </div>
                {application.alternativePhone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Alternative Phone</label>
                    <p className="mt-1 text-gray-900 flex items-center">
                      <FaPhone className="mr-2 text-gray-500" />
                      <a href={`tel:${application.alternativePhone}`} className="text-indigo-600 hover:underline">
                        {formatPhoneNumber(application.alternativePhone)}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaMapMarkerAlt className="mr-2 text-indigo-600" />
                Address Information
              </h3>
              <div className="space-y-2">
                <p className="text-gray-900">{application.streetAddress}</p>
                <p className="text-gray-900">
                  {application.city}, {application.state} {application.postalCode}
                </p>
                <p className="text-gray-900">{application.country}</p>
              </div>
            </div>

            {/* Document Status */}
            {application.documentsRequired && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaFileAlt className="mr-2 text-indigo-600" />
                  Document Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">Business License</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      application.documentsProvided?.businessLicense 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {application.documentsProvided?.businessLicense ? 'Provided' : 'Missing'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">Tax Document</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      application.documentsProvided?.taxDocument 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {application.documentsProvided?.taxDocument ? 'Provided' : 'Missing'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">Company Profile</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      application.documentsProvided?.companyProfile 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {application.documentsProvided?.companyProfile ? 'Provided' : 'Missing'}
                    </span>
                  </div>
                </div>

                {/* Document URLs */}
                {(application.documentUrls?.businessLicenseUrl || 
                  application.documentUrls?.taxDocumentUrl || 
                  application.documentUrls?.companyProfileUrl) && (
                  <div className="mt-4 space-y-2">
                    <h4 className="font-medium text-gray-900">Download Documents:</h4>
                    {application.documentUrls?.businessLicenseUrl && (
                      <a href={application.documentUrls.businessLicenseUrl} 
                         target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center px-3 py-1 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 mr-2 mb-2">
                        <FaDownload className="mr-1" /> Business License
                      </a>
                    )}
                    {application.documentUrls?.taxDocumentUrl && (
                      <a href={application.documentUrls.taxDocumentUrl} 
                         target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center px-3 py-1 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 mr-2 mb-2">
                        <FaDownload className="mr-1" /> Tax Document
                      </a>
                    )}
                    {application.documentUrls?.companyProfileUrl && (
                      <a href={application.documentUrls.companyProfileUrl} 
                         target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center px-3 py-1 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 mr-2 mb-2">
                        <FaDownload className="mr-1" /> Company Profile
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Verification Notes */}
            {application.verificationNotes && application.verificationNotes.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaStickyNote className="mr-2 text-indigo-600" />
                  Verification History
                </h3>
                <div className="space-y-3">
                  {application.verificationNotes.map((note, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(note.action)}`}>
                          {note.action?.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(note.timestamp)}
                        </span>
                      </div>
                      <p className="text-gray-900">{note.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-gray-700 font-medium">Applied At</label>
                  <p className="text-gray-900">{formatDate(application.submittedAt || application.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium">Last Updated</label>
                  <p className="text-gray-900">{formatDate(application.updatedAt)}</p>
                </div>
                {application.approvedAt && (
                  <div>
                    <label className="block text-gray-700 font-medium">Approved At</label>
                    <p className="text-gray-900">{formatDate(application.approvedAt)}</p>
                  </div>
                )}
                {application.rejectedAt && (
                  <div>
                    <label className="block text-gray-700 font-medium">Rejected At</label>
                    <p className="text-gray-900">{formatDate(application.rejectedAt)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Admin Actions */}
            {application.verificationStatus !== 'approved' && application.verificationStatus !== 'rejected' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Actions</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (optional)
                    </label>
                    <textarea
                      value={actionNotes}
                      onChange={(e) => setActionNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Add notes about your decision..."
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => onUpdateStatus(application.id, 'under_review', actionNotes)}
                      disabled={actionLoading}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <FaEye className="mr-2" />
                      Under Review
                    </button>
                    <button
                      onClick={() => onUpdateStatus(application.id, 'approved', actionNotes)}
                      disabled={actionLoading}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <FaCheck className="mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => onUpdateStatus(application.id, 'rejected', actionNotes)}
                      disabled={actionLoading}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      <FaTimes className="mr-2" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header with Stats Toggle */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Employer Verification</h2>
            <p className="text-gray-600">Review and manage employer applications</p>
          </div>
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <FaChartBar className="mr-2" />
            {showStats ? 'Hide Stats' : 'Show Stats'}
            {showStats ? <FaChevronUp className="ml-2" /> : <FaChevronDown className="ml-2" />}
          </button>
        </div>

        {/* Stats Dashboard */}
        {showStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-sm font-medium text-gray-600">Total Applications</h3>
              <p className="text-2xl font-bold text-indigo-600">{applications.length}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-yellow-200">
              <h3 className="text-sm font-medium text-gray-600">Pending Review</h3>
              <p className="text-2xl font-bold text-yellow-600">
                {applications.filter(a => a.verificationStatus === 'pending').length}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-green-200">
              <h3 className="text-sm font-medium text-gray-600">Approved</h3>
              <p className="text-2xl font-bold text-green-600">
                {applications.filter(a => a.verificationStatus === 'approved').length}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-red-200">
              <h3 className="text-sm font-medium text-gray-600">Rejected</h3>
              <p className="text-2xl font-bold text-red-600">
                {applications.filter(a => a.verificationStatus === 'rejected').length}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by company name, legal name, email, or contact person..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <FaBuilding className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
              <p className="text-gray-600">
                {applications.length === 0 
                  ? "No employer applications have been submitted yet."
                  : "No applications match your current filters."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Industry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Risk Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Documents
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applied
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <FaBuilding className="h-5 w-5 text-indigo-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {application.companyName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {application.legalName !== application.companyName && application.legalName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{application.contactPersonName}</div>
                        <div className="text-sm text-gray-500">{application.email}</div>
                        <div className="text-sm text-gray-500">{formatPhoneNumber(application.phone)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{application.industry}</div>
                        <div className="text-sm text-gray-500">{application.companySize}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(application.verificationStatus)}`}>
                          {application.verificationStatus?.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`flex items-center text-sm font-medium ${getRiskLevelColor(application.riskLevel)}`}>
                          <FaShieldAlt className="mr-1" />
                          {application.riskLevel?.toUpperCase()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {application.documentsRequired ? (
                          <div className="flex space-x-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              application.documentsProvided?.businessLicense 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              BL
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              application.documentsProvided?.taxDocument 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              TD
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              application.documentsProvided?.companyProfile 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              CP
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Not required</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(application.submittedAt || application.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedApplication(application);
                              setShowModal(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 flex items-center"
                            title="View Details"
                          >
                            <FaEye className="mr-1" />
                            View
                          </button>
                          
                          {application.verificationStatus === 'pending' && (
                            <>
                              <button
                                onClick={() => updateApplicationStatus(application.id, 'approved', '')}
                                className="text-green-600 hover:text-green-900 flex items-center"
                                title="Quick Approve"
                                disabled={actionLoading}
                              >
                                <FaCheck className="mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => updateApplicationStatus(application.id, 'under_review', '')}
                                className="text-blue-600 hover:text-blue-900 flex items-center"
                                title="Mark as Under Review"
                                disabled={actionLoading}
                              >
                                <FaEye className="mr-1" />
                                Review
                              </button>
                            </>
                          )}
                          
                          {application.website && (
                            <a
                              href={application.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-gray-900 flex items-center"
                              title="Visit Website"
                            >
                              <FaExternalLinkAlt />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination could be added here if needed */}
        {filteredApplications.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow-md">
            <div className="flex-1 flex justify-between sm:hidden">
              <span className="text-sm text-gray-700">
                Showing {filteredApplications.length} of {applications.length} applications
              </span>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{filteredApplications.length}</span> of{' '}
                  <span className="font-medium">{applications.length}</span> applications
                </p>
              </div>
              {/* Add pagination controls here if needed */}
            </div>
          </div>
        )}

        {/* Application Details Modal */}
        {showModal && selectedApplication && (
          <ApplicationDetailsModal
            application={selectedApplication}
            onClose={() => {
              setShowModal(false);
              setSelectedApplication(null);
              setActionNotes('');
            }}
            onUpdateStatus={updateApplicationStatus}
          />
        )}
      </div>
    </AdminLayout>
  );
};